import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import React from 'react';
import { ArrayPath, FieldArrayWithId, useFormContext } from 'react-hook-form';

import { ContactDTO, ST } from '_lib/api/apiTypes.shim';

import { Box } from '_lib/components/Box/Box';
import { Grid } from '_lib/components/Grid/Grid';
import { IconButton } from '_lib/components/IconButton/IconButton';

import { MenuItem } from '_lib/components/MenuItem/MenuItem';
import { SelectFormItem } from '_lib/components/SelectFormItem/SelectFormItem';
import {
  TextFieldFormItem,
  PhoneNumberTextFieldFormItem,
  TextFieldFormItemProps
} from '_lib/components/TextFieldFormItem/TextFieldFormItem';

interface ResourceWithContactDTOs {
  contacts?: ContactDTO[];
}

interface ResourceWithLocationCreateDTOs {
  locations: ResourceWithContactDTOs[];
}

type ParentResource = ResourceWithContactDTOs | ResourceWithLocationCreateDTOs;

const typeNameComponent = (contactTypeName: ST.ContactTypeEnum, props: TextFieldFormItemProps) => {
  if (contactTypeName === ST.ContactTypeEnum.Email) return <TextFieldFormItem {...props} />;
  return <PhoneNumberTextFieldFormItem {...props} />;
};

interface ContactListFormItemProps<T extends ParentResource> {
  contactFields: FieldArrayWithId<T, ArrayPath<T>>[];
  contactsPath: ArrayPath<T>;
  contactAppend: (contact: ContactDTO) => void;
  contactRemove: (index: number) => void;
  requireOneContact?: boolean;
  oneContactTypes?: ST.ContactTypeEnum[];
}

export const ContactListFormItem = <T extends ParentResource>(
  props: ContactListFormItemProps<T>
): React.ReactElement => {
  const { contactFields, contactsPath, contactAppend, contactRemove, requireOneContact, oneContactTypes } = props;

  const { control, watch, getValues } = useFormContext();

  return (
    <Box sx={{ paddingTop: 1 }}>
      {contactFields.map((field, index) => (
        <Grid key={field.id} container direction='row' spacing={1}>
          <Grid item xs={'auto'} sm={'auto'}>
            <SelectFormItem name={`${contactsPath}.${index}.type`} control={control} label='Type' required>
              {(
                (index === 0 && requireOneContact ? oneContactTypes : undefined) || Object.values(ST.ContactTypeEnum)
              ).map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </SelectFormItem>
          </Grid>
          <Grid item xs={true} sm={true}>
            {typeNameComponent(getValues(`${contactsPath}.${index}.type`), {
              name: `${contactsPath}.${index}.value`,
              label: watch(`${contactsPath}.${index}.type`),
              control: control,
              required: true
            })}
          </Grid>
          <Grid item xs={'auto'} sm={'auto'} sx={{ marginTop: 0.75 }}>
            <IconButton onClick={() => contactRemove(index)} disabled={index === 0 && requireOneContact}>
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Grid item xs={12} sm={12} container direction='row' sx={{ justifyContent: 'flex-end' }}>
        <IconButton onClick={() => contactAppend({ type: ST.ContactTypeEnum.MobilePhone, value: '' })}>
          <AddIcon />
        </IconButton>
      </Grid>
    </Box>
  );
};
